import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Sparkles,
  PhoneCall,
  Activity,
  Award,
  BookOpen,
  Search,
  ChevronRight,
  Filter
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";

const COLORS = ["#0EA5E9", "#10B981", "#F59E0B", "#F43F5E", "#8B5CF6", "#14B8A6"];

const AutoInsightsPage = () => {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flow, setFlow] = useState("Front Desk");
  const [version, setVersion] = useState("1.0");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get(`/dashboards/auto-insights?flow=${encodeURIComponent(flow)}&version=${version}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching auto insights:", error);
      toast.error("Error al cargar datos de Auto Insights de IA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [flow, version]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-bold text-slate-800">Auto Insights no disponibles</h2>
        <p className="text-slate-500 mt-2">Por favor, intente con otra combinación de filtros.</p>
      </div>
    );
  }

  const KPICard = ({ title, value, subtitle, icon: Icon, color = "sky" }) => {
    const colorClasses = {
      sky: "text-sky-600 bg-sky-100",
      emerald: "text-emerald-600 bg-emerald-100",
      purple: "text-purple-600 bg-purple-100"
    };

    return (
      <Card className="hover:shadow-md transition-all border-l-4 border-l-purple-500">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-extrabold mt-2 text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </Card>
    );
  };

  const filteredDetails = data.details_table?.filter(row =>
    row.agent?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.summary?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-8 rounded-3xl shadow-lg relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl transform translate-x-12 -translate-y-12"></div>
        
        <div className="space-y-2 relative z-10">
          <div className="flex items-center gap-2 bg-white/10 w-fit px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Generado Automáticamente por IA</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">Dashboard Auto Insights</h1>
          <p className="text-purple-100 text-sm max-w-xl">
            Análisis profundo y clasificación semántica de llamadas telefónicas utilizando modelos avanzados de procesamiento de lenguaje natural de OpenAI.
          </p>
        </div>

        {/* Dynamic Selectors */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 relative z-10">
          <div className="w-48 bg-white/10 rounded-xl backdrop-blur-md border border-white/20 p-1 text-white">
            <Select value={flow} onValueChange={setFlow}>
              <SelectTrigger className="border-none bg-transparent hover:bg-white/5 text-white placeholder-white/80 focus:ring-0">
                <SelectValue placeholder="Seleccionar Flujo" />
              </SelectTrigger>
              <SelectContent className="bg-white text-slate-800 shadow-xl rounded-xl">
                <SelectItem value="Front Desk">Front Desk</SelectItem>
                <SelectItem value="Facturas">Facturas</SelectItem>
                <SelectItem value="WhatsApp">WhatsApp / SMS</SelectItem>
                <SelectItem value="Servicio al Cliente">Servicio al Cliente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-32 bg-white/10 rounded-xl backdrop-blur-md border border-white/20 p-1 text-white">
            <Select value={version} onValueChange={setVersion}>
              <SelectTrigger className="border-none bg-transparent hover:bg-white/5 text-white placeholder-white/80 focus:ring-0">
                <SelectValue placeholder="Versión" />
              </SelectTrigger>
              <SelectContent className="bg-white text-slate-800 shadow-xl rounded-xl">
                <SelectItem value="1.0">v1.0 (Activa)</SelectItem>
                <SelectItem value="1.1">v1.1 (Beta)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title={flow === "WhatsApp" ? "Mensajes con Autoinsight" : "Llamadas con Autoinsight"}
          value={data.total_calls?.toLocaleString()}
          subtitle="Procesadas en total"
          icon={PhoneCall}
          color="sky"
        />
        <KPICard
          title={flow === "WhatsApp" ? "Conversaciones de Chat" : "Horas de Llamadas Autoinsight"}
          value={flow === "WhatsApp" ? `${data.total_calls} chats` : `${data.total_hours} h`}
          subtitle={flow === "WhatsApp" ? "Mensajes analizados por IA" : "Duración acumulada analizada"}
          icon={Activity}
          color="emerald"
        />
        <KPICard
          title="Precisión de Categorización"
          value="98.7%"
          subtitle="Evaluación del modelo"
          icon={Award}
          color="purple"
        />
      </div>

      {/* Visual Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Widget 1: Clasificación de la Gestión */}
        {data.classification && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-slate-800">Clasificación de la Gestión</CardTitle>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded font-medium">Gráfico de Pastel</span>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.classification}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {data.classification.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Widget 2: First Call Resolution */}
        {data.fcr && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-slate-800">First Call Resolution (FCR) - Auto</CardTitle>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded font-medium">Resolución</span>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.fcr}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]}>
                      {data.fcr.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 1) % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Widget 3: Adherencia al Modelo de Servicio */}
        {data.adherence && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-100">
              <CardTitle className="text-lg font-bold text-slate-800">Adherencia al Modelo de Servicio</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.adherence}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      dataKey="value"
                    >
                      {data.adherence.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Widget 4: Conocimiento del Proceso */}
        {data.knowledge && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-100">
              <CardTitle className="text-lg font-bold text-slate-800">Conocimiento del Proceso</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.knowledge} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#10B981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Bottom Insights Table */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <CardTitle className="text-lg font-bold text-slate-800">Tabla Detalle de FCR e Interacciones Inteligentes</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Análisis semántico extraído automáticamente de la llamada.</p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar agente o resumen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 w-full bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>
            <Button variant="outline" size="sm" className="flex items-center gap-2 border-slate-200 text-slate-700">
              <Filter className="w-4 h-4" />
              <span>Filtrar</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Agente</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Línea</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resumen Automático (IA)</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Ver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDetails.length > 0 ? (
                  filteredDetails.map((row, idx) => (
                    <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-700 font-bold text-sm">
                            {row.agent?.charAt(0)}
                          </div>
                          <span className="font-semibold text-slate-900 text-sm">{row.agent}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          row.line === "Cliente" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"
                        }`}>
                          {row.line}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">{row.date}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-md truncate">{row.summary}</td>
                      <td className="px-6 py-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 font-semibold"
                          onClick={() => navigate("/calls")}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-400 text-sm">
                      No se encontraron resultados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutoInsightsPage;
